import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import Pagination from "src/components/pagination";
import usePagination from "src/customHooks/usePagination";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";
import { SlotRequestsFilters } from "../components/slot-requests-filters";
import { SlotRequestsTableSkeleton } from "../components/SlotRequestsTableSkeleton";
import SlotRequestsTable from "../components/slot-requests-table";
import { SlotRequestsFilter, useCountSlotRequestsQuery, useGetSlotRequestsQuery } from "../slot-requests.service";
import { SlotRequest } from "../types/slot-request.entity";

const parseFilters = (searchParams: URLSearchParams): SlotRequestsFilter => {
  const query: SlotRequestsFilter = {};
  const requestId = searchParams.get("requestId");
  const learnerId = searchParams.get("learnerId");
  const coachId = searchParams.get("coachId");

  if (requestId) query.requestId = requestId;
  if (learnerId) query.learnerId = learnerId;
  if (coachId) query.coachId = coachId;
  return query;
};

const serializeFilters = (state: SlotRequestsFilter) => {
  const params = new URLSearchParams();
  if (state.requestId) params.set("requestId", state.requestId);
  if (state.learnerId) params.set("learnerId", state.learnerId);
  if (state.coachId) params.set("coachId", state.coachId);
  return params;
};

const SlotRequestsPage = () => {
  const tableSearch = usePaginatedTableSearchParams<SlotRequestsFilter>({
    parseState: parseFilters,
    serializeState: serializeFilters,
  });
  const initialQuery = tableSearch.state;
  const { data, numberOfPages, totalCount, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<SlotRequest & { id: string }>({
      useGetDataQuery: useGetSlotRequestsQuery,
      useCountData: useCountSlotRequestsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Slot Requests" />
      <SlotRequestsFilters key={tableSearch.syncKey} initialQuery={initialQuery} updateQuery={tableSearch.replaceFilters} />

      {isSuccess ? (
        <>
          <div className="mb-3 flex items-center justify-between text-sm text-slate-600">
            <span>{typeof totalCount === "number" ? `${totalCount} request${totalCount === 1 ? "" : "s"}` : "Slot requests"}</span>
            <span className="text-xs text-slate-400">Ordered by requested date</span>
          </div>
          {showTableSkeleton ? (
            <div className="mb-6 overflow-x-auto border border-slate-200 bg-white shadow-sm">
              <table className="min-w-[1160px] w-full">
                <tbody><SlotRequestsTableSkeleton rows={10} /></tbody>
              </table>
            </div>
          ) : <SlotRequestsTable data={data ?? []} />}
          <Pagination numberOfPages={numberOfPages || 1} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <div className="border border-red-200 bg-red-50 p-4 text-sm text-red-700">Could not load slot requests. Please try again.</div>
      ) : <PageLoading />}
    </div>
  );
};

export default SlotRequestsPage;
